// Pure identifier-detection helpers for the homepage/SERP search box.
// Kept in a standalone module so they can be unit-tested without mounting the
// Vue component.

import { parseId } from '@/openalexId';
import { entityConfigs } from '@/entityConfigs';

/**
 * Detect a pasted/typed OpenAlex entity ID and return its parsed form so the
 * search box can jump straight to the entity page (zd#8363), the way DOI/ORCID
 * already do. Returns the parseId() result `{ entityType, shortId, isNative,
 * normalized }` or null if the input isn't a native OpenAlex ID.
 *
 * Accepts the forms a user would paste:
 *   "A5017453014"                                  -> authors/a5017453014
 *   "https://openalex.org/A5017453014"             -> authors/a5017453014
 *   "https://openalex.org/authors/A5017453014"     -> authors/a5017453014
 *   "W2163605009", "S137773608", "I27837315", ...  -> works/sources/institutions/...
 *
 * Guards against hijacking ordinary searches:
 *  - single token only (a multi-word query that merely starts with an ID-shaped
 *    word is a real search, not an identifier lookup);
 *  - native entity types only (W/A/I/S/P/F/C/T/G), not namespaced externals
 *    like "sdgs/1";
 *  - the numeric part must be >=4 digits, so short tokens like "a5", "t2" or
 *    "c19" stay regular searches. (Real OpenAlex IDs are far longer.)
 *
 * The caller still verifies the ID resolves against the API before navigating,
 * so a non-existent-but-ID-shaped token falls through to a normal search.
 */
export function extractOpenalexId(str) {
  if (!str) return null;
  const trimmed = String(str).trim();
  if (!trimmed || /\s/.test(trimmed)) return null;

  const parsed = parseId(trimmed);
  if (!parsed || !parsed.isNative) return null;

  const digits = parsed.shortId.replace(/^[a-z]/i, '');
  if (!/^\d{4,}$/.test(digits)) return null;

  return parsed;
}

/**
 * Detect a pasted location identifier so the locations SERP search box can
 * jump straight to that location's page (oxjob #852) — the record-lookup
 * affordance for repository managers ("paste your own record ID"). Location
 * short ids are `<namespace>:<native_id>` (doi:10.1038/…, pmh:/neu:cj82kt84h,
 * mag:999999992); they're opaque, case-significant, and slash-bearing, so this
 * NEVER case-folds anything except the namespace prefix itself.
 *
 * Accepts the forms a repository manager would paste:
 *   "doi:10.1038/nature12373"                          -> doi:10.1038/nature12373
 *   "10.1038/nature12373"                              -> doi:10.1038/nature12373  (bare DOI)
 *   "https://doi.org/10.1038/nature12373"              -> doi:10.1038/nature12373
 *   "pmh:/neu:cj82kt84h", "PMH:oai:HAL:hal-01234"      -> pmh:… (namespace lowercased)
 *   "mag:999999992", "pmid:12345678"                   -> as-is
 *   "https://openalex.org/locations/doi:10.1038/x"     -> doi:10.1038/x  (full URL)
 *
 * Guards: single token only (any internal whitespace = a real search);
 * mag/pmid must be all digits. The caller still verifies the id resolves
 * against the /locations singleton before navigating, so a lookalike falls
 * through to a normal search.
 */
export function extractLocationId(str) {
  if (!str) return null;
  const trimmed = String(str).trim();
  if (!trimmed || /\s/.test(trimmed)) return null;

  // Full location URL — strip the prefix, keep the id verbatim.
  const urlMatch = trimmed.match(/^https?:\/\/openalex\.org\/locations\/(.+)$/i);
  if (urlMatch) return urlMatch[1];

  // DOI in its pasteable forms → doi:<doi>.
  const doiUrlMatch = trimmed.match(/^https?:\/\/(?:dx\.)?doi\.org\/(10\..+)$/i);
  if (doiUrlMatch) return `doi:${doiUrlMatch[1]}`;
  if (/^10\.\d{4,}/.test(trimmed) && trimmed.includes('/')) return `doi:${trimmed}`;

  // Namespaced id — lowercase ONLY the namespace, preserve the rest.
  const nsMatch = trimmed.match(/^(doi|pmh|mag|pmid|openalex_curation):(.+)$/i);
  if (nsMatch) {
    const ns = nsMatch[1].toLowerCase();
    const rest = nsMatch[2];
    if ((ns === 'mag' || ns === 'pmid') && !/^\d+$/.test(rest)) return null;
    if (ns === 'doi' && !/^10\.\d{4,}/.test(rest)) return null;
    return `${ns}:${rest}`;
  }

  return null;
}

/**
 * Detect an ISSN-shaped query and normalize it to the canonical NNNN-NNNC form
 * (hyphenated, uppercase check digit). Returns null if the input isn't an ISSN.
 *
 * Designed to be paste-tolerant: ISSNs copied from journal pages, PDFs, or
 * Wikipedia frequently arrive with a Unicode dash (en-dash, non-breaking
 * hyphen, minus sign) instead of an ASCII hyphen, with a non-breaking/zero-width
 * space, or wrapped in a label / parenthetical. Typing always produces a clean
 * ASCII hyphen, which is why typing worked but pasting didn't (zd#8095).
 *
 * Accepts e.g.:
 *   "2041-1723"            -> "2041-1723"
 *   "20411723"             -> "2041-1723"   (missing hyphen)
 *   "2041–1723"       -> "2041-1723"   (en-dash)
 *   "ISSN: 2041-1723"      -> "2041-1723"   (labeled)
 *   "eISSN 2041-1723"      -> "2041-1723"
 *   "2041-1723 (Online)"   -> "2041-1723"   (trailing parenthetical)
 *   "1234-567X"            -> "1234-567X"   (X check digit)
 */
export function extractIssn(str) {
  if (!str) return null;

  let s = String(str)
    // Normalize the many Unicode hyphen/dash variants to an ASCII hyphen:
    // hyphen, non-breaking hyphen, figure/en/em dashes (U+2010–U+2015),
    // minus sign (U+2212), and the full-width hyphen (U+FF0D).
    .replace(/[‐-―−－]/g, '-')
    // Drop zero-width characters that can ride along on a paste.
    .replace(/[​-‍﻿]/g, '')
    // Non-breaking space -> regular space.
    .replace(/ /g, ' ')
    .trim();

  // Strip an optional leading label: "ISSN", "eISSN", "pISSN", "Online ISSN",
  // "Print ISSN", "ISSN-L", with optional colon and surrounding space.
  s = s.replace(/^(?:e|p|online|print)?\s*issn(?:-l)?\s*:?\s*/i, '').trim();

  // Exact ISSN, with or without the hyphen: NNNN-NNNC or NNNNNNNC.
  let m = s.match(/^(\d{4})-?(\d{3}[\dxX])$/);
  if (m) return `${m[1]}-${m[2].toUpperCase()}`;

  // Hyphenated ISSN embedded in surrounding text, e.g. "2041-1723 (Online)".
  // Require the hyphen here so we don't treat an arbitrary 8-digit run inside a
  // longer query as an ISSN; bound it by non-digits on both sides.
  m = s.match(/(?:^|[^\d-])(\d{4})-(\d{3}[\dxX])(?![\d-])/);
  if (m) return `${m[1]}-${m[2].toUpperCase()}`;

  return null;
}

/**
 * True if the query contains a `*` or `?` wildcard OUTSIDE of a quoted phrase.
 *
 * A wildcard only works on the no-stem (exact) field: on the default stemmed
 * search the engine strips the literal prefix at index time and silently returns
 * wrong results, so it rejects the query (#364). The advised fix — the
 * `search.exact=` param — can't be typed into the search box (it just becomes
 * part of the search string and loops the same error, zd#9012). So the search box
 * detects an unquoted wildcard and auto-routes to the exact field instead.
 *
 * Mirrors the backend `_has_unquoted_wildcard` (openalex-elastic-api
 * core/search.py): strip `"quoted phrases"` first (a wildcard inside quotes is a
 * separate, adjacency/proximity case the engine handles on its own), then a
 * wildcard in any remaining whitespace-separated token counts.
 */
export function hasUnquotedWildcard(str) {
  if (!str || typeof str !== 'string') return false;
  const unquoted = str.replace(/"[^"]*"/g, ' ');
  return unquoted.split(/\s+/).some(word => word.includes('*') || word.includes('?'));
}

// Entity nouns that can open an OQL query ("works where …", "authors sort by …").
const OQL_ENTITY_NOUNS = new Set(Object.keys(entityConfigs));

/**
 * Cheap sniff: does this text LOOK like an OQL query rather than a search
 * phrase? (oxjob #598) Used by the search box to decide whether a paste/submit
 * is worth a server /validate round-trip; only a VALID parse actually routes
 * the user to the OQL tab, so this can afford rare false positives (one wasted
 * validate call) but must stay quiet on ordinary prose.
 *
 * Fires only when ALL hold:
 *  - the whole (whitespace-normalized) text starts with a known entity noun
 *    (optionally prefixed with "get"), e.g. "works", "authors";
 *  - followed by a clause opener: `where`, `sort by`, `group by`, or `sample`;
 *  - a `where` body also shows at least one operator signal (parens, a
 *    comparator, or an operator/verb word) — so prose that happens to start
 *    "works where …" without any query machinery stays a plain search.
 *
 * Deliberately never fires on: a bare entity noun ("works" is valid OQL but an
 * overwhelmingly more likely search term), identifiers (DOI/ORCID/OpenAlex
 * ID — no entity-noun prefix), and wildcard searches.
 */
export function looksLikeOql(str) {
  if (!str || typeof str !== 'string') return false;
  // Pasted OQL is often pretty-printed — normalize all whitespace to single spaces.
  const s = str.trim().replace(/\s+/g, ' ');
  // The new OQL is one sentence that starts with `get` (`get works about kelp since 2020`,
  // `get the count of works ... by year`, oxjob #1555): the server's validate decides
  if (/^get\s+\S+\s+\S+/i.test(s)) return true;
  const body = s.replace(/^get\s+/i, '');
  const m = body.match(/^([a-z][a-z-]*)\s+(.+)$/i);
  if (!m) return false;
  if (!OQL_ENTITY_NOUNS.has(m[1].toLowerCase())) return false;
  const rest = m[2].toLowerCase();
  if (/^where\s/.test(rest)) {
    return /[()]|>=|<=|[<>]|\bis\b|\bhas\b|\bmatches\b|\bcites\b|\bcited by\b|\brelated to\b/.test(rest);
  }
  return /^(sort by|group by|sample)\s/.test(rest);
}

// Words that open a question or a request (oxjob #1744). Kept short: words that also start
// common topic or journal names ("research ...", "studies in ...", "top quark ...") are left out.
const QUESTION_OPENERS = new Set([
  'what', 'which', 'who', 'whose', 'how', 'when', 'where', 'why',
  'is', 'are', 'was', 'were', 'do', 'does', 'did', 'can', 'could', 'should',
  'show', 'find', 'list', 'compare', 'give', 'tell', 'count',
  'papers', 'works', 'articles', 'publications',
]);

/** Lowercase, strip accents and punctuation: "University of California, San Diego" -> "university of california san diego". */
function nameKey(str) {
  return latinize(str).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

/**
 * Does the typed text read as a sentence (a question for the chat) rather than a name or a
 * keyword? (oxjob #1744) Under the new search the homepage box sends a question to the chat,
 * so it stops suggesting keywords once this is true. A client heuristic, instant on every keystroke:
 *   - a `?` that ends a word ("what is CRISPR?"; a wildcard `?` sits inside a word: "wom?n");
 *   - 4 or more words;
 *   - 3 or more words opening with a question or request word ("which journals retract").
 * Never a question: identifiers (DOI, ORCID, OpenAlex ID, ISSN, URL), a quoted phrase, OQL.
 * Entity names can be long ("university of california san diego"), so a word-count match is
 * overruled while `names` (the suggestions on screen; not work titles, which a sentence often
 * prefixes) still holds a name that starts with the typed text.
 */
export function looksLikeQuestion(text, names = []) {
  if (!text || typeof text !== 'string') return false;
  const s = text.trim();
  if (!s || s.startsWith('"')) return false;
  if (/^(https?:\/\/|www\.)/i.test(s) || /\b\d{4}-\d{4}-\d{4}-\d{3}[\dX]\b/i.test(s)) return false;
  if (extractOpenalexId(s) || extractLocationId(s) || extractIssn(s) || looksLikeOql(s)) return false;
  if (/\?(\s|$)/.test(s)) return true;
  const words = s.split(/\s+/);
  const opens = QUESTION_OPENERS.has(nameKey(words[0]));
  if (words.length < 4 && !(opens && words.length >= 3)) return false;
  const typed = nameKey(s);
  return !names.some(n => nameKey(n).startsWith(typed));
}

// One-shot cross-instance focus request (#598 r4): the landing-page search box,
// on an empty-Enter, navigates to the SERP and asks the SERP's own SearchBox
// instance (mounted on the next route) to grab focus so the user can just type.
// Module-level because the two boxes are different component instances.
let _focusSearchBoxOnMount = false;
export function requestSearchBoxFocus() {
  _focusSearchBoxOnMount = true;
}
export function consumeSearchBoxFocus() {
  const f = _focusSearchBoxOnMount;
  _focusSearchBoxOnMount = false;
  return f;
}

// --- Split-profile author detection (oxjob #820) ---
// The search-box dropdown collapses author suggestions that share a display
// name to the single highest-works profile (dedupeByName). For a researcher
// whose works are split across several profiles that reads as "OpenAlex is
// missing my papers": they click the one suggestion and see a fraction of
// their work. These helpers detect the collapse so the dropdown can offer a
// door to the full Authors search, where every matching profile is listed.

/** Strip diacritics so "Mélanie" matches "Melanie". */
export function latinize(str) {
  return String(str ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** At least one query word must appear fully in the display_name (latinized, lowercased). */
export function authorNameMatchesQuery(displayName, query) {
  const nameNorm = latinize(displayName || '').toLowerCase();
  const queryWords = latinize(query).toLowerCase().split(/\s+/).filter(Boolean);
  return queryWords.some(word => nameNorm.includes(word));
}

/** Collapse same-name items to the one with the highest works_count. */
export function dedupeByName(items) {
  const map = new Map();
  for (const item of items) {
    const key = (item.display_name || '').toLowerCase();
    const existing = map.get(key);
    if (!existing || (item.works_count || 0) > (existing.works_count || 0)) {
      map.set(key, item);
    }
  }
  return [...map.values()];
}

/**
 * The dropdown footer's search target (#820 r3). The footer offers the full
 * SERP search ("Search works for …") — normally on the current entity type,
 * but when EVERY visible suggestion is one homogeneous type from the swap set
 * (authors / sources / institutions), the query evidently reads as that kind
 * of thing (e.g. a person's name), so the footer targets that entity instead —
 * putting the full ranked list (every matching author profile included) one
 * Enter away. Mixed lists, empty lists, and non-swappable types (keywords,
 * topics, works) all fall through to the current entity type.
 */
const FOOTER_SWAP_TYPES = new Set(['authors', 'sources', 'institutions']);
// Intent classes that mean "run what I typed as a works search": never swap the footer
// for these (oxjob #1347; CNRS "Climate OR change" → Institutions misfire).
const FOOTER_NO_SWAP_INTENTS = new Set(['boolean', 'title', 'question', 'identifier', 'topic']);
const INTENT_TO_ENTITY = { author: 'authors', institution: 'institutions', source: 'sources' };
// Confidence (top prob minus runner-up) at which the classifier's word overrides the suggestion
// rule. Measured on Opus-labelled real queries (oxjob #1347 EXPLORE § 4): ≥ 0.6 ≈ 0.87 accuracy.
export const INTENT_VETO_CONFIDENCE = 0.6;

/**
 * Where the dropdown footer sends Enter. Without an intent, the #820 r3 rule: swap to
 * authors/sources/institutions when EVERY visible suggestion is that one type. With an intent
 * (oxjob #1347, {label, confidence} from the in-browser classifier, or null while it loads):
 *   - a confident "this is a works query" intent vetoes the swap;
 *   - a confident author/institution/source intent swaps when at least one suggestion is that type.
 */
export function footerSearchEntityType(suggestionTypes, currentEntityType, intent = null) {
  const types = suggestionTypes || [];
  const confident = intent && intent.confidence >= INTENT_VETO_CONFIDENCE;
  if (confident && FOOTER_NO_SWAP_INTENTS.has(intent.label)) return currentEntityType;
  if (confident && INTENT_TO_ENTITY[intent.label] && currentEntityType === 'works') {
    const want = INTENT_TO_ENTITY[intent.label];
    if (types.includes(want)) return want;
  }
  if (types.length > 0) {
    const t = types[0];
    if (FOOTER_SWAP_TYPES.has(t) && types.every(x => x === t)) return t;
  }
  return currentEntityType;
}
